import { defineStore } from 'pinia';
import { computed, ref, toRaw } from 'vue';
import {
    ICoachingForm,
    ICoachingFormAnswer,
    CoachingFormAnswerType,
    ICoachingFormReasonCode,
    ICoachingFormQuestion,
    ICoachingFormChange,
} from '../services/api/coachingForm/types';
import { temporaryUUID } from '@/js/composables/useTemporaryUUID';
import { transformReasonCodesToNestedReasonCodes } from '../services/api/coachingForm/utils';
import { ILanguage } from './app';

export const useEditCoachingFormStore = defineStore('edit-coaching-form', () => {
    const sortForm = ref(false);

    const formInitiated = ref(false);

    const formChangeLog = ref<ICoachingFormChange[]>([]);

    const formDraft = ref<ICoachingForm | null>();

    // TODO: add Language type
    const initiateForm = (language: ILanguage) => {
        formDraft.value = {
            id: temporaryUUID(),
            title: '',
            useAi: false,
            useAction: false,
            useApprovalRating: false,
            useReasonCodes: false,
            useOpenComment: false,
            useRestrictedComment: false,
            isActive: true,
            language,
            sections: [],
        };
    };

    const cleanUp = () => {
        formDraft.value = null;
        formChangeLog.value = [];
        formInitiated.value = false;
        sortForm.value = false;
    };

    const loadForm = (form: ICoachingForm) => {
        let clonedForm = structuredClone(toRaw(form));

        clonedForm.useOpenComment = clonedForm.openComment ? true : false;
        clonedForm.useRestrictedComment = clonedForm.restrictedComment ? true : false;
        clonedForm.useReasonCodes = clonedForm.reasonCodes ? true : false;
        clonedForm.useApprovalRating = clonedForm.approvalRatings ? true : false;

        clonedForm.sections.forEach((section) => {
            section.questions.forEach((question) => {
                const notApplicableAnswer = question.answers.find((answer) => answer.notApplicable);
                question.notApplicable = notApplicableAnswer ? true : false;
                question.notApplicableLabel = notApplicableAnswer?.label;
                question.answers = question.answers.filter((answer) => !answer.notApplicable);
            });
        });

        formDraft.value = clonedForm;
    };

    const registerChange = (change: ICoachingFormChange) => {
        if (!formChangeLog.value.includes(change)) {
            formChangeLog.value.push(change);
        }
    };

    const assertForm = () => {
        if (!formDraft.value) {
            throw new Error('Form is not loaded');
        }

        return formDraft.value;
    };

    const addAnswer = (question: ICoachingFormQuestion, answer: ICoachingFormAnswer) => {
        if (!answer.id) {
            answer.id = temporaryUUID();
        }

        question.answers.push(answer);

        return answer;
    };

    const addQuestion = (sectionIndex: number, question?: ICoachingFormQuestion) => {
        const formState = assertForm();

        if (!question) {
            question = {
                id: temporaryUUID(),
                title: '',
                description: '',
                prompt: '',
                isCritical: false,
                useComments: false,
                notApplicable: false,
                answerType: CoachingFormAnswerType.Affirmative,
                answers: [],
            };
        }

        registerChange('QuestionAdded');

        formState.sections[sectionIndex].questions.push(question);

        return question;
    };

    const removeQuestion = (sectionIndex: number, questionIndex: number) => {
        const formState = assertForm();

        registerChange('QuestionRemoved');

        formState.sections[sectionIndex].questions.splice(questionIndex, 1);
    };

    const addSection = () => {
        const section = {
            id: temporaryUUID(),
            title: '',
            questions: [],
            useComments: false,
        };

        const formState = assertForm();

        registerChange('SectionAdded');

        formState.sections.push(section);

        return section;
    };

    const removeSection = (sectionIndex: number) => {
        const formState = assertForm();

        registerChange('SectionRemoved');

        formState.sections.splice(sectionIndex, 1);
    };

    const sortMoveUpQuestion = (sectionIndex: number, questionIndex: number) => {
        const formState = assertForm();

        if (questionIndex <= 0) {
            return;
        }

        registerChange('QuestionsOrder');

        const question = formState.sections[sectionIndex].questions.splice(questionIndex, 1)[0];
        formState.sections[sectionIndex].questions.splice(questionIndex - 1, 0, question);
    };

    const sortMoveDownQuestion = (sectionIndex: number, questionIndex: number) => {
        const formState = assertForm();

        if (questionIndex >= formState.sections[sectionIndex].questions.length - 1) {
            return;
        }

        registerChange('QuestionsOrder');

        const question = formState.sections[sectionIndex].questions.splice(questionIndex, 1)[0];
        formState.sections[sectionIndex].questions.splice(questionIndex + 1, 0, question);
    };

    const sortMoveUpSection = (sectionIndex: number) => {
        const formState = assertForm();

        if (sectionIndex <= 0) {
            return;
        }

        registerChange('SectionsOrder');

        const section = formState.sections.splice(sectionIndex, 1)[0];
        formState.sections.splice(sectionIndex - 1, 0, section);
    };

    const sortMoveDownSection = (sectionIndex: number) => {
        const formState = assertForm();

        if (sectionIndex >= formState.sections.length - 1) {
            return;
        }

        registerChange('SectionsOrder');

        const section = formState.sections.splice(sectionIndex, 1)[0];
        formState.sections.splice(sectionIndex + 1, 0, section);
    };

    const isLastSection = (sectionIndex: number) => {
        const formState = assertForm();

        return sectionIndex === formState.sections?.length - 1;
    };

    const isLastQuestion = (sectionIndex: number, questionIndex: number) => {
        const formState = assertForm();

        return questionIndex === formState.sections[sectionIndex]?.questions.length - 1;
    };

    const addReasonCode = (parentId: string | undefined, code: string) => {
        const formState = assertForm();

        formState.reasonCodes = formState.reasonCodes || {
            title: '',
            threshold: 0,
            description: '',
            reasonCodeDescription: '',
            codes: [],
        };

        const reasonCode = {
            id: temporaryUUID(),
            parentId,
            code,
        };

        formState.reasonCodes.codes.push(reasonCode);

        return reasonCode;
    };

    const removeReasonCode = (codeId: string) => {
        const formState = assertForm();

        if (!formState.reasonCodes) {
            return;
        }

        formState.reasonCodes.codes = formState.reasonCodes.codes.filter(
            (code) => code.id !== codeId
        );
    };

    const addApprovalRating = () => {
        const formState = assertForm();

        if (!formState.approvalRatings) {
            formState.approvalRatings = {
                criticalQuestionFallbackRatingId: '',
                ratings: [],
            };
        }

        const approvalRating = {
            id: temporaryUUID(),
            name: '',
            minPoints: 0,
            maxPoints: 0,
            requireApprovalBeforeDisplay: false,
        };

        formState.approvalRatings.ratings.push(approvalRating);

        return approvalRating;
    };

    const removeApprovalRating = (ratingId: string) => {
        const formState = assertForm();

        if (!formState.approvalRatings) {
            return;
        }

        formState.approvalRatings.ratings = formState.approvalRatings.ratings.filter(
            (rating) => rating.id !== ratingId
        );
    };

    const nestedReasonCodes = computed<ICoachingFormReasonCode[]>(() => {
        const codes = (formDraft.value?.reasonCodes?.codes || []) as ICoachingFormReasonCode[];

        return transformReasonCodesToNestedReasonCodes(codes);
    });

    const formPoints = computed(() => {
        if (!formDraft.value) {
            return 0;
        }

        return formDraft.value.sections.reduce(
            (total, section) =>
                total +
                section.questions.reduce(
                    (sectionTotal, question) =>
                        (Math.max(...question.answers.map((answer) => answer.points)) || 0) +
                        sectionTotal,
                    0
                ),
            0
        );
    });

    return {
        formDraft,
        sortForm,
        nestedReasonCodes,
        formChangeLog,
        formPoints,
        formInitiated,
        loadForm,
        cleanUp,
        initiateForm,
        registerChange,
        isLastSection,
        isLastQuestion,
        addAnswer,
        addQuestion,
        removeQuestion,
        addSection,
        removeSection,
        sortMoveUpSection,
        sortMoveDownSection,
        sortMoveUpQuestion,
        sortMoveDownQuestion,
        addReasonCode,
        removeReasonCode,
        addApprovalRating,
        removeApprovalRating,
    };
});
